import { defineStore } from 'pinia'
import { tnApi } from '@/api'
import { ref, computed } from 'vue'
import { DateTime } from 'luxon'
import { notify } from 'notiwind'
import sortOn from 'sort-on'
import { v4 as uuidv4 } from 'uuid'
import { isNumber } from 'lodash'

interface INotificationPayload {
  id: number | string,
  type: string
  sound?: string | null
  timeout: number
  title: string
  close: boolean
  text: string
  icon?: string | null
  route?: object | null
  extraPayload?: any | null
  variant?: string
  timestamp: String | null | DateTime
  button?: string | null
  notify: boolean
  center: boolean
  group?: string,
  action?: Function | null
}

interface INotification {
  id: number | string,
  data: INotificationPayload,
  key: string,
  read: boolean,
  read_at?: DateTime | null,
  created_at: DateTime | String | null,
  updated_at?: DateTime,
  unread: boolean
  uid?: number,
  type: string
}

interface IProgress {
  id: string,
  total: number,
  current: number,
  info?: string,
  close: Function
}

const defaultOptions = {
  type: 'notice',
  sound: false,
  timeout: 10000,
  title: null,
  close: true,
  text: '',
  icon: null,
  timestamp: DateTime.now().toISO(),
  route: null,
  extraPayload: null,
  variant: 'success',
  button: null,
  center: true,
  notify: true,
  group: 'default',
  action: () => {}
}

export const useNotificationStore = defineStore('notification', () => {
  const {
    loadNotifications,
    notificationSetRead,
    notificationSetReadAll
  } = tnApi(true)

  const store = useNotificationStore()
  const centerNotification = ref<INotification[]>([])
  const notificationHistory = ref<INotification[]>([])

  const progresses = ref<IProgress[]>([])

  const setOptions = (options: INotificationPayload) => {
    return Object.assign({}, defaultOptions, options)
  }

  const removeNotification = async (id: number | string) => {
    store.$patch((state) => {
      state.notificationHistory = notificationHistory.value.filter(item => item.id !== id)
      state.centerNotification = centerNotification.value.filter(item => item.id !== id)
    })

    if (isNumber(id)) {
      await notificationSetRead(id)
    }
  }

  const markAllNotificationsAsRead = async () => {
    store.$patch((state) => {
      state.notificationHistory = notificationHistory.value.map(item => {
        item.read = true
        item.unread = false
        return item
      })
      state.centerNotification = centerNotification.value.map(item => {
        item.read = true
        item.unread = false
        return item
      })
    })
    await notificationSetReadAll()
  }

  const sendNotice = (title = 'Benachrichtigung', content = {}, type = 'notice', timeout = 10000, group = 'default', id: number | string) => {
    const result = notify({
      group,
      id,
      title,
      type,
      text: content
    }, timeout)
    return result
  }

  const sendNoticeByPayload = (payload: INotificationPayload, id: number | string) => {
    const result = sendNotice(payload.title, payload, payload.type, payload.timeout, payload.group, id)
    return result
  }

  const addSpinnerNotification = (title: string) => {
    const payload: INotificationPayload = {
      id: uuidv4(),
      type: 'spinner',
      sound: null,
      timeout: -1,
      title,
      close: false,
      text: title,
      icon: null,
      timestamp: DateTime.now().toISO(),
      notify: false,
      center: false
    }

    const result = sendNotice(title, payload, 'spinner', payload.timeout, payload.group, payload.id)
    return result
  }

  const updateProgressNotification = (id: string, current: number, total?: number) => {
    const index = progresses.value.findIndex(item => item.id === id)
    if (index !== -1) {
      store.$patch((state) => {
        if (total !== undefined) {
          state.progresses[index].total = total
        }
        state.progresses[index].current = current
      })
    }
  }

  const getProgressById = (id: string) => {
    const progress = progresses.value.find(item => item.id === id)
    if (progress) {
      return { total: progress.total, current: progress.current, close: progress.close }
    }
  }

  const addProgressNotification = (id: string, title: string, total: number, current: number) => {
    const progress = progresses.value.find(item => item.id === id)
    if (!progress) {
      const payload: INotificationPayload = {
        id,
        type: 'progress',
        sound: null,
        timeout: -1,
        title,
        close: false,
        text: title,
        icon: null,
        timestamp: DateTime.now().toISO(),
        notify: false,
        center: false
      }

      const result = sendNotice(title, payload, 'progress', payload.timeout, payload.group, payload.id)
      progresses.value.push({
        id,
        total,
        current,
        close: result
      })

      return result
    }
  }

  const addNotification = (notification: INotification, notify: boolean = true) => {
    const index = centerNotification.value.findIndex(item => item.id === notification.id)
    notification.data.id = notification.id
    if (index === -1 && notification.unread) {
      notification.data.timestamp = notification.created_at
      notification.data = setOptions(notification.data)

      notificationHistory.value.push(notification)

      if (notification.data.center) {
        centerNotification.value.push(notification)
      }

      if (notify && notification.data.notify) {
        sendNoticeByPayload(notification.data, notification.id)
      }
    }
  }

  const addClientNotification = (options: INotificationPayload, type: string = 'internal.network', notify: boolean = true, center: boolean = false) => {
    options.center = center

    if (type === 'internal.network') {
      const countInternalNetwork = notificationHistory.value.filter(item => item.type === 'internal.network').length
      if (countInternalNetwork > 0) {
        notify = false
      }
    }

    if (notify || options.center) {
      const newNotification: INotification = {
        id: uuidv4(),
        data: setOptions(options),
        key: 'internal',
        created_at: DateTime.now().toISO(),
        type,
        read: false,
        unread: true
      }

      addNotification(newNotification, notify)
    }
  }

  const addUndoNotification = (title: string, extraPayload: object, action: Function = () => {}) => {
    const id = uuidv4()
    const timestamp = DateTime.now().toISO()
    const options: INotificationPayload = {
      id,
      timeout: 5000,
      title,
      timestamp,
      center: false,
      close: true,
      notify: true,
      type: 'notice',
      variant: 'undo',
      text: '',
      button: 'Aktion rückgängig machen',
      extraPayload,
      action
    }

    const newNotification: INotification = {
      id,
      data: setOptions(options),
      key: 'ui',
      created_at: DateTime.now().toISO(),
      type: 'undo-notice',
      read: false,
      unread: true
    }
    addNotification(newNotification, true)
  }

  const unreadNotifications = computed(() => {
    const unread: INotificationPayload[] = centerNotification.value.filter((item) => item.unread).map(item => item.data)
    return sortOn(unread, 'timestamp').reverse()
  })

  const unreadNotificationsCount = computed(() => unreadNotifications.value.length)
  const hasUnreadNotifications = computed(() => unreadNotificationsCount.value > 0)

  const updateNotifications = async () => {
    if (!centerNotification.value.length) {
      await loadFromServer()
    }

    sessionStorage.setItem('notifications', JSON.stringify(centerNotification.value))
    store.$patch((state) => {
      state.centerNotification = centerNotification.value
    })
  }

  const loadFromServer = async () => {
    const data = await loadNotifications()
    notificationHistory.value = []
    if (data !== undefined) {
      const { notifications } = data
      for (const notifcation of notifications) {
        addNotification(notifcation, false)
      }
    }
  }

  return { centerNotification, getProgressById, updateProgressNotification, progresses, addProgressNotification, notificationHistory, addClientNotification, addNotification, addSpinnerNotification, addUndoNotification, sendNotice, updateNotifications, hasUnreadNotifications, unreadNotificationsCount, unreadNotifications, markAllNotificationsAsRead, removeNotification }
})
